import type { ApiClient } from './client.js';
import type { TodoDayDto, TurnInAssignmentDto, TurnInSubjectDto } from './types.js';

/**
 * Planner API for the 4–8 Home: per-day To-Do lists and per-subject Turn-In
 * assignments. Date params are ISO YYYY-MM-DD. Range reads (`from`/`to`)
 * back the weekly settings editor; the live page reads a single day.
 */
export interface PlannerApi {
  // To-Do
  listTodo(classroomId: string, from: string, to: string): Promise<TodoDayDto[]>;
  putTodoDay(classroomId: string, date: string, lines: string[]): Promise<TodoDayDto>;
  // Turn-In subjects
  listSubjects(classroomId: string): Promise<TurnInSubjectDto[]>;
  createSubject(classroomId: string, name: string): Promise<TurnInSubjectDto>;
  renameSubject(classroomId: string, subjectId: string, name: string): Promise<TurnInSubjectDto>;
  reorderSubjects(classroomId: string, orderedIds: string[]): Promise<void>;
  removeSubject(classroomId: string, subjectId: string): Promise<void>;
  // Turn-In assignments
  listAssignments(classroomId: string, from: string, to: string): Promise<TurnInAssignmentDto[]>;
  putAssignment(
    classroomId: string,
    subjectId: string,
    date: string,
    assignment: string,
  ): Promise<TurnInAssignmentDto>;
}

export function createPlannerApi(client: ApiClient): PlannerApi {
  return {
    async listTodo(classroomId, from, to) {
      const res = await client.get<{ days: TodoDayDto[] }>(
        `/api/classrooms/${classroomId}/todo?from=${from}&to=${to}`,
      );
      return res.days;
    },
    async putTodoDay(classroomId, date, lines) {
      const res = await client.put<{ day: TodoDayDto }>(
        `/api/classrooms/${classroomId}/todo/${date}`,
        { lines },
      );
      return res.day;
    },
    async listSubjects(classroomId) {
      const res = await client.get<{ subjects: TurnInSubjectDto[] }>(
        `/api/classrooms/${classroomId}/turn-in/subjects`,
      );
      return res.subjects;
    },
    async createSubject(classroomId, name) {
      const res = await client.post<{ subject: TurnInSubjectDto }>(
        `/api/classrooms/${classroomId}/turn-in/subjects`,
        { name },
      );
      return res.subject;
    },
    async renameSubject(classroomId, subjectId, name) {
      const res = await client.patch<{ subject: TurnInSubjectDto }>(
        `/api/classrooms/${classroomId}/turn-in/subjects/${subjectId}`,
        { name },
      );
      return res.subject;
    },
    async reorderSubjects(classroomId, orderedIds) {
      await client.put(`/api/classrooms/${classroomId}/turn-in/subjects/order`, { orderedIds });
    },
    async removeSubject(classroomId, subjectId) {
      await client.delete(`/api/classrooms/${classroomId}/turn-in/subjects/${subjectId}`);
    },
    async listAssignments(classroomId, from, to) {
      const res = await client.get<{ assignments: TurnInAssignmentDto[] }>(
        `/api/classrooms/${classroomId}/turn-in/assignments?from=${from}&to=${to}`,
      );
      return res.assignments;
    },
    async putAssignment(classroomId, subjectId, date, assignment) {
      const res = await client.put<{ assignment: TurnInAssignmentDto }>(
        `/api/classrooms/${classroomId}/turn-in/subjects/${subjectId}/assignments/${date}`,
        { assignment },
      );
      return res.assignment;
    },
  };
}
